import { BBox, type EntityId } from '@lakaran/geometry';

/**
 * Uniform grid broad-phase index. Cheap to update and good enough for drawings with
 * hundreds of thousands of entities; queries only touch the cells a viewport overlaps.
 */
export class SpatialIndex {
  private readonly cells = new Map<string, EntityId[]>();
  private readonly boxes = new Map<EntityId, BBox>();
  private readonly huge: EntityId[] = [];

  constructor(private cellSizeMm = 250) {}

  get size(): number {
    return this.boxes.size;
  }

  get cellSize(): number {
    return this.cellSizeMm;
  }

  insert(id: EntityId, box: BBox): void {
    if (this.boxes.has(id)) this.remove(id);
    if (box.isEmpty) return;
    this.boxes.set(id, box);
    const range = this.cellRange(box);
    if (!range) {
      this.huge.push(id);
      return;
    }
    for (let cx = range.minX; cx <= range.maxX; cx++) {
      for (let cy = range.minY; cy <= range.maxY; cy++) {
        const key = `${cx}:${cy}`;
        const list = this.cells.get(key);
        if (list) list.push(id);
        else this.cells.set(key, [id]);
      }
    }
  }

  update(id: EntityId, box: BBox): void {
    this.insert(id, box);
  }

  remove(id: EntityId): void {
    const box = this.boxes.get(id);
    if (!box) return;
    this.boxes.delete(id);
    const hugeIndex = this.huge.indexOf(id);
    if (hugeIndex >= 0) {
      this.huge.splice(hugeIndex, 1);
      return;
    }
    const range = this.cellRange(box);
    if (!range) return;
    for (let cx = range.minX; cx <= range.maxX; cx++) {
      for (let cy = range.minY; cy <= range.maxY; cy++) {
        const key = `${cx}:${cy}`;
        const list = this.cells.get(key);
        if (!list) continue;
        const index = list.indexOf(id);
        if (index >= 0) list.splice(index, 1);
        if (list.length === 0) this.cells.delete(key);
      }
    }
  }

  query(box: BBox): EntityId[] {
    const found = new Set<EntityId>();
    for (const id of this.huge) found.add(id);
    const range = this.cellRange(box);
    if (!range) return [...found];
    for (let cx = range.minX; cx <= range.maxX; cx++) {
      for (let cy = range.minY; cy <= range.maxY; cy++) {
        const list = this.cells.get(`${cx}:${cy}`);
        if (!list) continue;
        for (const id of list) found.add(id);
      }
    }
    return [...found];
  }

  clear(): void {
    this.cells.clear();
    this.boxes.clear();
    this.huge.length = 0;
  }

  /** Rebuilds the grid with a cell size that suits the current drawing extent. */
  repartition(cellSizeMm: number): void {
    const entries = [...this.boxes.entries()];
    this.cells.clear();
    this.huge.length = 0;
    this.cellSizeMm = Math.max(cellSizeMm, 1e-3);
    this.boxes.clear();
    for (const [id, box] of entries) this.insert(id, box);
  }

  /** Cell size that yields roughly 4x4 cells for the given extent. */
  static suggestCellSize(extent: BBox): number {
    const span = Math.max(extent.width, extent.height, 1);
    return Math.max(span / 4, 1e-3);
  }

  private cellRange(box: BBox): { minX: number; minY: number; maxX: number; maxY: number } | null {
    const s = this.cellSizeMm;
    const minX = Math.floor(box.minX / s);
    const minY = Math.floor(box.minY / s);
    const maxX = Math.floor(box.maxX / s);
    const maxY = Math.floor(box.maxY / s);
    const cellCount = (maxX - minX + 1) * (maxY - minY + 1);
    if (!Number.isFinite(cellCount) || cellCount > 20000) return null;
    return { minX, minY, maxX, maxY };
  }
}
